import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Bike, Check, ShoppingBag } from 'lucide-react'
import { formatarReais } from '@du/shared'
import { Button, Divider, Escolha, GrupoEscolhas, Input, SectionTitle, Textarea } from '@du/ui'
import { api, ErroApi, type Bairro, type Loja } from '../lib/api.js'
import { paraApi, useCarrinho } from '../lib/carrinho.js'
import { useCotacao } from '../lib/cotacao.js'
import { lerOrigem } from '../lib/origem.js'

type Tipo = 'ENTREGA' | 'RETIRADA'
/**
 * UMA opção de Pix, não duas.
 *
 * Quando o Pix online está ligado, "Pix" significa pagar agora, com código na
 * tela; quando não está, significa o Pix manual de sempre. Mostrar as duas
 * opções lado a lado faria o cliente escolher entre coisas que, para ele, têm
 * o mesmo nome — e metade escolheria a errada.
 */
type OpcaoPagamento = { valor: string; rotulo: string; apoio?: string | undefined }

function pagamentosDisponiveis(pixOnline: boolean): OpcaoPagamento[] {
  return [
    { valor: 'DINHEIRO', rotulo: 'Dinheiro' },
    { valor: 'CARTAO_ENTREGA', rotulo: 'Cartão na entrega', apoio: 'O motoboy leva a maquininha' },
    pixOnline
      ? { valor: 'PIX_ONLINE', rotulo: 'Pix', apoio: 'Código na próxima tela' }
      : { valor: 'PIX_MANUAL', rotulo: 'Pix' },
  ]
}

/** Chave de idempotência por rascunho: sobrevive a recarregar a página. */
function chaveIdempotencia(): string {
  const CHAVE = 'du:idem'
  try {
    const salva = sessionStorage.getItem(CHAVE)
    if (salva) return salva
    const nova = `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
    sessionStorage.setItem(CHAVE, nova)
    return nova
  } catch {
    return `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
  }
}

export function Checkout({
  loja,
  bairros,
  zap,
}: {
  loja: Loja | null
  bairros: Bairro[]
  zap?: { telefone: string | null; nome: string | null; enderecos: {
    rua: string; numero: string; complemento: string | null
    referencia: string | null; bairroTexto: string; cep: string | null
    bairroId: string | null
  }[] } | null
}) {
  const navegar = useNavigate()
  const { itens, limpar } = useCarrinho()

  const [tipo, setTipo] = useState<Tipo>('ENTREGA')
  const [nome, setNome] = useState('')
  const [telefone, setTelefone] = useState('')
  const [bairroId, setBairroId] = useState('')
  const [rua, setRua] = useState('')
  const [numero, setNumero] = useState('')
  const [complemento, setComplemento] = useState('')
  const [referencia, setReferencia] = useState('')
  const [bairroTexto, setBairroTexto] = useState('')
  const [pagamento, setPagamento] = useState<string>('DINHEIRO')
  const [trocoPara, setTrocoPara] = useState('')
  const [observacao, setObservacao] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  /**
   * Preenche com o que a loja já sabe, quando o cliente veio do WhatsApp.
   *
   * Só preenche campo VAZIO: se a pessoa já começou a digitar outro
   * endereço, sobrescrever seria apagar o que ela fez na frente dela.
   */
  useEffect(() => {
    if (!zap) return
    if (zap.nome) setNome((v) => v || zap.nome!)
    if (zap.telefone) setTelefone((v) => v || zap.telefone!)
    const e = zap.enderecos[0]
    if (e) {
      setRua((v) => v || e.rua)
      setNumero((v) => v || e.numero)
      setComplemento((v) => v || e.complemento || '')
      setReferencia((v) => v || e.referencia || '')
      if (e.bairroId) setBairroId((v) => v || e.bairroId!)
    }
  }, [zap])

  // Delivery pausado tira a opção da tela em vez de deixar a pessoa preencher
  // o endereço inteiro para só então descobrir que não dá.
  useEffect(() => {
    if (loja && (!loja.abertaDelivery || loja.deliveryPausado)) setTipo('RETIRADA')
  }, [loja])

  /**
   * O bairro só é OBRIGATÓRIO quando a loja cobra por bairro.
   *
   * Nos modos RAIO e HÍBRIDO quem define a taxa é a distância, calculada pelo
   * servidor a partir da rua e do número. Exigir o bairro ali era pedir ao
   * cliente algo que o servidor não usa — e, com a lista de bairros vazia,
   * isso travava a entrega pelo site inteira: a tela dizia "nenhum bairro
   * cadastrado, escolha retirada" enquanto o cálculo por raio funcionava
   * perfeitamente.
   */
  const bairroObrigatorio = loja?.modoTaxa === 'BAIRRO'

  /**
   * Só manda o endereço quando ele tem chance de ser encontrado.
   *
   * Cada cotação com endereço é uma consulta de geocoding — cacheada no
   * servidor, mas ainda assim uma ida. Esperar rua com 3+ letras e número
   * preenchido evita gastar isso a cada tecla, e antes disso a taxa padrão é
   * um palpite tão bom quanto qualquer outro.
   */
  const enderecoParaCotar =
    tipo === 'ENTREGA' && rua.trim().length >= 3 && numero.trim().length >= 1
      ? {
          rua: rua.trim(),
          numero: numero.trim(),
          ...(bairroTexto.trim() ? { bairroTexto: bairroTexto.trim() } : {}),
        }
      : undefined

  const { cotacao, erro: erroCotacao } = useCotacao(itens, {
    tipo,
    ...(bairroId ? { bairroId } : {}),
    ...(enderecoParaCotar ? { endereco: enderecoParaCotar } : {}),
  })

  const trocoCents = useMemo(() => {
    const limpo = trocoPara.replace(/[^\d]/g, '')
    return limpo ? Number(limpo) : null
  }, [trocoPara])

  const trocoInsuficiente =
    pagamento === 'DINHEIRO' && trocoCents !== null && cotacao !== null && trocoCents < cotacao.totalCents

  const podeEnviar =
    itens.length > 0 &&
    nome.trim().length >= 2 &&
    telefone.replace(/\D/g, '').length >= 10 &&
    cotacao !== null &&
    !erroCotacao &&
    !trocoInsuficiente &&
    (tipo === 'RETIRADA' ||
      ((!bairroObrigatorio || bairroId !== '') &&
        rua.trim().length >= 3 &&
        numero.trim().length >= 1))

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!podeEnviar) return
    setErro(null)
    setEnviando(true)
    try {
      const r = await api<{ codigoPublico: string; numeroDia: number }>('/v1/publico/pedidos', {
        method: 'POST',
        headers: { 'idempotency-key': chaveIdempotencia() },
        corpo: {
          itens: paraApi(itens),
          tipo,
          cliente: { nome: nome.trim(), telefone: telefone.replace(/\D/g, '') },
          formaPagamento: pagamento,
          ...(pagamento === 'DINHEIRO' && trocoCents ? { trocoParaCents: trocoCents } : {}),
          ...(observacao.trim() ? { observacao: observacao.trim() } : {}),
          ...(lerOrigem() ? { origem: lerOrigem() } : {}),
          ...(tipo === 'ENTREGA'
            ? {
                endereco: {
                  rua: rua.trim(),
                  numero: numero.trim(),
                  /**
                   * Só manda o bairro QUANDO EXISTE — igual à cotação, dez
                   * linhas acima.
                   *
                   * A API valida `bairroId` com min(1), então string vazia é
                   * recusada. E ela fica vazia sempre que a loja cobra por
                   * distância e não cadastrou bairro nenhum, que é
                   * exatamente a configuração do Dú. Resultado: o cliente
                   * preenchia tudo, via o total certo, apertava enviar e
                   * levava "Dados inválidos." — 100% dos pedidos de entrega
                   * pelo site morriam no último clique, e a cotação logo
                   * antes tinha funcionado, o que tornava o defeito
                   * invisível para quem testava só a tela.
                   */
                  ...(bairroId ? { bairroId } : {}),
                  ...(bairroTexto.trim() ? { bairroTexto: bairroTexto.trim() } : {}),
                  ...(complemento.trim() ? { complemento: complemento.trim() } : {}),
                  ...(referencia.trim() ? { referencia: referencia.trim() } : {}),
                },
              }
            : {}),
        },
      })
      limpar()
      // A chave some com o pedido criado: o próximo pedido precisa de uma nova.
      try {
        sessionStorage.removeItem('du:idem')
      } catch {
        /* sem sessionStorage, a chave já era aleatória por requisição */
      }
      navegar(`/pedido/${r.codigoPublico}`, { replace: true })
    } catch (x) {
      setErro(x instanceof ErroApi ? x.message : 'Não consegui enviar. Confira a internet e tente de novo.')
      setEnviando(false)
    }
  }

  if (itens.length === 0) {
    return (
      <div className="bloco nada">
        <p>Seu carrinho está vazio.</p>
        <Link to="/">
          <Button variante="secundario">Ver o cardápio</Button>
        </Link>
      </div>
    )
  }

  const bairro = bairros.find((b) => b.id === bairroId)

  return (
    <form className="bloco checkout" onSubmit={enviar}>
      <SectionTitle variante="laranja">Finalizar pedido</SectionTitle>

      <GrupoEscolhas nome="Como você quer receber?" min={1} max={1} obrigatorio escolhidos={1}>
        {loja?.abertaDelivery && !loja.deliveryPausado ? (
          <Escolha
            tipo="radio"
            nome="tipo"
            valor="ENTREGA"
            rotulo="Entrega"
            descricao={
              bairro
                ? `${bairro.nome} · ${formatarReais(bairro.taxaCents)} · ~${bairro.tempoEstimadoMin} min`
                : bairroObrigatorio
                  ? 'Escolha o bairro abaixo'
                  : 'A taxa é calculada pela distância'
            }
            marcada={tipo === 'ENTREGA'}
            onMudar={() => setTipo('ENTREGA')}
          />
        ) : null}
        <Escolha
          tipo="radio"
          nome="tipo"
          valor="RETIRADA"
          rotulo="Retirar na loja"
          descricao={loja ? `${loja.endereco.rua}, ${loja.endereco.numero}` : undefined}
          marcada={tipo === 'RETIRADA'}
          onMudar={() => setTipo('RETIRADA')}
        />
      </GrupoEscolhas>

      <Input rotulo="Seu nome" value={nome} onChange={(e) => setNome(e.target.value)} required autoComplete="name" />
      <Input
        rotulo="WhatsApp"
        prefixo="+55"
        placeholder="11 99702-5250"
        inputMode="tel"
        autoComplete="tel"
        dica="É por aqui que avisamos quando o pedido sai"
        value={telefone}
        onChange={(e) => setTelefone(e.target.value)}
        required
      />

      {tipo === 'ENTREGA' ? (
        <>
          {bairros.length > 0 ? (
            <>
              <Divider tom="sutil" />
              <GrupoEscolhas
                nome="Bairro"
                min={bairroObrigatorio ? 1 : 0}
                max={1}
                obrigatorio={bairroObrigatorio}
                escolhidos={bairroId ? 1 : 0}
              >
                {bairros.map((b) => (
                  <Escolha
                    key={b.id}
                    tipo="radio"
                    nome="bairro"
                    valor={b.id}
                    rotulo={b.nome}
                    descricao={`~${b.tempoEstimadoMin} min`}
                    precoExtra={formatarReais(b.taxaCents)}
                    marcada={bairroId === b.id}
                    onMudar={() => setBairroId(b.id)}
                  />
                ))}
              </GrupoEscolhas>
            </>
          ) : bairroObrigatorio ? (
            /* Só aqui a mensagem faz sentido: a loja cobra POR bairro e não
               cadastrou nenhum, então não há como calcular a taxa. */
            <>
              <Divider tom="sutil" />
              <p className="nada">
                Nenhum bairro cadastrado ainda. Escolha retirada ou chame no WhatsApp.
              </p>
            </>
          ) : null}
          <Input rotulo="Rua" value={rua} onChange={(e) => setRua(e.target.value)} required autoComplete="address-line1" />
          {bairros.length === 0 && !bairroObrigatorio ? (
            /* Não entra no cálculo da taxa — entra na comanda, para o motoboy
               achar o lugar. Por isso é opcional: endereço com rua e número
               já basta para o servidor, e um campo a mais obrigatório é mais
               um motivo para desistir. */
            <Input
              rotulo="Bairro"
              value={bairroTexto}
              onChange={(e) => setBairroTexto(e.target.value)}
              placeholder="Opcional, ajuda na entrega"
              autoComplete="address-level3"
            />
          ) : null}
          <div className="dois">
            <Input rotulo="Número" value={numero} onChange={(e) => setNumero(e.target.value)} required inputMode="numeric" />
            <Input rotulo="Complemento" value={complemento} onChange={(e) => setComplemento(e.target.value)} placeholder="ap, bloco" />
          </div>
          <Input
            rotulo="Ponto de referência"
            value={referencia}
            onChange={(e) => setReferencia(e.target.value)}
            placeholder="Portão azul, ao lado da padaria"
            dica="Ajuda o entregador a achar na primeira"
          />
        </>
      ) : null}

      <Divider tom="sutil" />
      <GrupoEscolhas nome="Pagamento" min={1} max={1} obrigatorio escolhidos={1}>
        {pagamentosDisponiveis(loja?.pixOnline ?? false).map((p) => (
          <Escolha
            key={p.valor}
            tipo="radio"
            nome="pagamento"
            valor={p.valor}
            rotulo={p.rotulo}
            {...(p.apoio ? { descricao: p.apoio } : {})}
            marcada={pagamento === p.valor}
            onMudar={() => setPagamento(p.valor)}
          />
        ))}
      </GrupoEscolhas>

      {pagamento === 'DINHEIRO' ? (
        <Input
          rotulo="Troco para quanto?"
          prefixo="R$"
          inputMode="numeric"
          placeholder="Deixe vazio se não precisa"
          dica="Digite em centavos: 5000 = R$ 50,00"
          value={trocoPara}
          onChange={(e) => setTrocoPara(e.target.value)}
          {...(trocoInsuficiente ? { erro: 'O valor é menor que o total do pedido.' } : {})}
        />
      ) : null}

      <Textarea
        rotulo="Observação do pedido"
        placeholder="Interfone não funciona, entregar na portaria…"
        maxLength={300}
        value={observacao}
        onChange={(e) => setObservacao(e.target.value)}
      />

      <Divider />

      <div className="resumo">
        <div className="resumo__linha">
          <span>Subtotal</span>
          <span>{cotacao ? formatarReais(cotacao.subtotalCents) : '…'}</span>
        </div>
        {cotacao && cotacao.taxaEntregaCents > 0 ? (
          <div className="resumo__linha">
            <span>Taxa de entrega</span>
            <span>{formatarReais(cotacao.taxaEntregaCents)}</span>
          </div>
        ) : null}
        <div className="resumo__linha resumo__linha--total">
          <span>Total</span>
          <strong>{cotacao ? formatarReais(cotacao.totalCents) : '…'}</strong>
        </div>
        {cotacao ? (
          <p className="resumo__prazo">Previsão de {cotacao.tempoEstimadoMin} minutos</p>
        ) : null}
      </div>

      {erroCotacao ? <p className="erro-faixa">{erroCotacao.message}</p> : null}
      {erro ? <p className="erro-faixa">{erro}</p> : null}

      <Button
        type="submit"
        tamanho="lg"
        larguraTotal
        icone={tipo === 'ENTREGA' ? Bike : ShoppingBag}
        iconeDireita={Check}
        disabled={!podeEnviar}
        carregando={enviando}
      >
        Enviar pedido
      </Button>
      <Link to="/carrinho" className="voltar">
        Voltar ao carrinho
      </Link>
    </form>
  )
}
